// apps/codeframe/src/motion/model.ts
import type { MotionTimeline, MotionTrack, MotionLayer, MotionChannel, Keyframe, MotionProp, EaseSpec, PlaybackConfig } from '../store/types';

let _seq = 0;
function genId(prefix: string): string {
  _seq += 1;
  return `${prefix}_${Date.now().toString(36)}_${_seq.toString(36)}`;
}

export function emptyTimeline(): MotionTimeline {
  return { duration: 0, tracks: [], playback: {} };
}

function maxKeyframeTime(tl: MotionTimeline): number {
  let max = 0;
  for (const tr of tl.tracks) for (const ly of tr.layers) for (const ch of ly.channels) for (const k of ch.keyframes) if (k.t > max) max = k.t;
  return max;
}

function mapTrack(tl: MotionTimeline, trackId: string, fn: (t: MotionTrack) => MotionTrack): MotionTimeline {
  return { ...tl, tracks: tl.tracks.map((t) => (t.id === trackId ? fn(t) : t)) };
}
function mapLayer(tr: MotionTrack, layerId: string, fn: (l: MotionLayer) => MotionLayer): MotionTrack {
  return { ...tr, layers: tr.layers.map((l) => (l.id === layerId ? fn(l) : l)) };
}

export function addTrack(tl: MotionTimeline, opts: { target: string; label?: string }): { timeline: MotionTimeline; trackId: string; layerId: string } {
  const trackId = genId('tr');
  const layerId = genId('ly');
  const track: MotionTrack = { id: trackId, target: opts.target, label: opts.label, layers: [{ id: layerId, blend: 'replace', channels: [] }] };
  return { timeline: { ...tl, tracks: [...tl.tracks, track] }, trackId, layerId };
}

export function removeTrack(tl: MotionTimeline, trackId: string): MotionTimeline {
  return { ...tl, tracks: tl.tracks.filter((t) => t.id !== trackId) };
}

export function addLayer(tl: MotionTimeline, trackId: string, blend: 'replace' | 'add'): { timeline: MotionTimeline; layerId: string } {
  const layerId = genId('ly');
  const timeline = mapTrack(tl, trackId, (t) => ({ ...t, layers: [...t.layers, { id: layerId, blend, channels: [] }] }));
  return { timeline, layerId };
}

export function setLayerBlend(tl: MotionTimeline, trackId: string, layerId: string, blend: 'replace' | 'add'): MotionTimeline {
  return mapTrack(tl, trackId, (t) => mapLayer(t, layerId, (l) => ({ ...l, blend })));
}

export function setKeyframe(tl: MotionTimeline, a: { trackId: string; layerId: string; prop: MotionProp; t: number; value: number | string; ease: EaseSpec }): MotionTimeline {
  const next = mapTrack(tl, a.trackId, (tr) => mapLayer(tr, a.layerId, (ly) => {
    const existing = ly.channels.find((c) => c.prop === a.prop);
    const kf: Keyframe = { t: a.t, value: a.value, ease: a.ease };
    let channels: MotionChannel[];
    if (!existing) {
      channels = [...ly.channels, { prop: a.prop, keyframes: [kf] }];
    } else {
      const kfs = existing.keyframes.filter((k) => k.t !== a.t).concat(kf).sort((x, y) => x.t - y.t);
      channels = ly.channels.map((c) => (c.prop === a.prop ? { ...c, keyframes: kfs } : c));
    }
    return { ...ly, channels };
  }));
  return { ...next, duration: Math.max(next.duration, maxKeyframeTime(next)) };
}

export function moveKeyframe(tl: MotionTimeline, a: { trackId: string; layerId: string; prop: MotionProp; fromT: number; toT: number }): MotionTimeline {
  const next = mapTrack(tl, a.trackId, (tr) => mapLayer(tr, a.layerId, (ly) => ({
    ...ly,
    channels: ly.channels.map((c) => (c.prop !== a.prop ? c : { ...c, keyframes: c.keyframes.map((k) => (k.t === a.fromT ? { ...k, t: a.toT } : k)).sort((x, y) => x.t - y.t) })),
  })));
  return { ...next, duration: Math.max(next.duration, maxKeyframeTime(next)) };
}

export function removeKeyframe(tl: MotionTimeline, a: { trackId: string; layerId: string; prop: MotionProp; t: number }): MotionTimeline {
  return mapTrack(tl, a.trackId, (tr) => mapLayer(tr, a.layerId, (ly) => ({
    ...ly,
    channels: ly.channels
      .map((c) => (c.prop !== a.prop ? c : { ...c, keyframes: c.keyframes.filter((k) => k.t !== a.t) }))
      .filter((c) => c.keyframes.length > 0),
  })));
}

export function setEase(tl: MotionTimeline, a: { trackId: string; layerId: string; prop: MotionProp; t: number; ease: EaseSpec }): MotionTimeline {
  return mapTrack(tl, a.trackId, (tr) => mapLayer(tr, a.layerId, (ly) => ({
    ...ly,
    channels: ly.channels.map((c) => (c.prop !== a.prop ? c : { ...c, keyframes: c.keyframes.map((k) => (k.t === a.t ? { ...k, ease: a.ease } : k)) })),
  })));
}

export function setDuration(tl: MotionTimeline, duration: number): MotionTimeline {
  return { ...tl, duration: Math.max(0, duration) };
}

export function setPlayback(tl: MotionTimeline, playback: PlaybackConfig): MotionTimeline {
  return { ...tl, playback };
}
